import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';

/**
 * Shared toolbar control styling for the Entity Management surfaces.
 *
 * The structure chart and the registry each grew their own copy of these, which is how
 * their toggle buttons ended up 2px apart in height. One definition, every tab.
 *
 * **The 14px is not a prototype size, it is the app's.** The theme sets inputs to 14px
 * Wotfard-Regular through `MuiTextField`'s `&&& .MuiInputBase-input`, and a bare `Select`
 * is not a `TextField` — it misses that rule and falls back to `body1`, which this theme
 * never overrides, so MUI's 16px default applies. Leaving the size out therefore does not
 * inherit the app's type; it puts a 16px `Select` next to a 14px `TextField` in the same
 * row. Restating the rule at the theme's own `&&&` specificity is what makes the two match.
 */

/**
 * The app's corner, from `cardBorderStyles`. Capsules keep their own geometry.
 * Two forms of one number: css takes a length, the export's SVG `rx` takes a number.
 */
export const EM_RADIUS_PX = 4;
export const EM_RADIUS = `${EM_RADIUS_PX}px`;

/**
 * Spread onto a Select or TextField that sits outside a form container.
 *
 * **Not for a modal.** The forms are built from `FormFieldsContainer`, which already sets
 * the input size this restates — so inside one this is redundant, and it was removed from
 * all of them. What is left are the inline panel controls (`RelationshipEditControls`,
 * `AllocationSection`) and one toolbar select, which have no such container above them: a
 * bare `Select` there misses the theme's `MuiTextField` rule and renders two pixels larger
 * than every other input in the app.
 */
export const EM_CONTROL_SX = {
  background: colors.primary[0],
  fontFamily: 'Wotfard-Regular',
  fontSize: '14px',
  '&&& .MuiInputBase-input': { fontFamily: 'Wotfard-Regular', fontSize: '14px' },
  '& .MuiSelect-select': { py: '6px' },
  '& .MuiOutlinedInput-input': { py: '6px' },
} as const;

/**
 * Spread onto the shared `SearchInput`.
 *
 * Deliberately without the control padding: `SearchInput` sets its own input padding
 * through `slotProps.htmlInput`, which lands on the element itself and would win
 * anyway. Letting it keep its own metrics is the point — it should look like the
 * search box on every other page, not like a locally-restyled one.
 */
export const EM_SEARCH_SX = { background: colors.primary[0] } as const;

/**
 * Everything a toggle in this module shares except its horizontal padding.
 *
 * `sx` on a group targets its children while `sx` on a single button targets itself, so
 * the standalone and grouped forms have to be written separately — this is the one
 * definition they are both written from, which is what stops them drifting apart.
 */
const toggleBaseSx = {
  // Matches the selects and chips beside it. Without a floor the toggle sizes to its own
  // 11px text and lands 3px short, which is exactly the drift this file exists to stop.
  minHeight: '32px',
  py: '4px',
  fontSize: '11px',
  fontWeight: 500,
  // Separates a relationship dot from its label; harmless on a toggle with no icon.
  gap: '5px',
  // MUI's unselected toggle is 54% black, which washes out beside a selected one and makes
  // the colour difference read as a weight difference.
  '&:not(.Mui-selected)': { color: colors.neutral[70] },
  // After the rule above, which would otherwise outrank MUI's disabled colour and leave a
  // greyed segment drawn as dark as a live one.
  '&.Mui-disabled': { color: colors.neutral[40] },
} as const;

/**
 * The module's toggle. Padded to sit comfortably beside a select of the same height.
 *
 * Selection is bold here and flat on the chart's compact toggles, which is deliberate.
 * These are modes — one of three ways to browse, where the chosen one is the heading for
 * everything below it. The chart's are lenses over the same graph, read as a set, and a
 * bold one there just looked like the labels had drifted apart.
 */
export const EM_TOGGLE_BUTTON_SX = {
  ...toggleBaseSx,
  px: '12px',
  '&.Mui-selected': { fontWeight: 600, color: colors.primary[100] },
} as const;

/**
 * The chart's toggles, and only the chart's.
 *
 * Six lenses, the metric select and the routing pair share one row there. At the padding
 * every other toggle uses, that row wraps onto three lines at the width the chart is
 * normally read at — measured, not guessed — and a wrapped row splits a single control
 * group in two. The width these give up is also what lets the metric select show a full
 * metric name.
 */
export const EM_TOGGLE_COMPACT_SX = { ...toggleBaseSx, px: '6px' } as const;

/**
 * The provenance chips beside the toolbar's selects.
 *
 * MUI's small chip is 24px, which against a 40px select reads as two unrelated controls
 * rather than one row. Sized to sit between the toggle buttons and the select instead.
 */
export const EM_TOOLBAR_CHIP_SX = {
  height: '32px',
  borderRadius: '16px',
  borderColor: colors.neutral[20],
  '& .MuiChip-label': { px: '10px', fontSize: '12px' },
} as const;

/**
 * A toggle that stands alone rather than in a group.
 *
 * Carries the corner radius the group gives its own ends — kept out of the base so a
 * segmented group doesn't round every seam inside it.
 */
export const EM_TOGGLE_SOLO_SX = { ...EM_TOGGLE_BUTTON_SX, borderRadius: EM_RADIUS } as const;

/** The standalone form of the chart's compact toggle. */
export const EM_TOGGLE_COMPACT_SOLO_SX = { ...EM_TOGGLE_COMPACT_SX, borderRadius: EM_RADIUS } as const;

/**
 * Only the group's ends round; the seams between segments stay square.
 *
 * `pointer-events` is restored on the disabled segments so a Tooltip on the button itself
 * still fires; a span around one segment cannot anchor it without breaking the pill. A
 * disabled button ignores the click either way. What the restored events would also bring
 * back is MUI's hover tint, which makes a dead segment look live when it is pressed, so
 * a disabled segment paints no hover.
 */
const groupEndRadiusSx = {
  '& .MuiToggleButton-root.Mui-disabled': {
    pointerEvents: 'auto',
    '&:hover': { backgroundColor: 'transparent' },
  },
  '& .MuiToggleButton-root:first-of-type': {
    borderTopLeftRadius: EM_RADIUS,
    borderBottomLeftRadius: EM_RADIUS,
  },
  '& .MuiToggleButton-root:last-of-type': {
    borderTopRightRadius: EM_RADIUS,
    borderBottomRightRadius: EM_RADIUS,
  },
} as const;

/** Applied to a ToggleButtonGroup — same geometry, reached through the group. */
export const EM_TOGGLE_GROUP_SX = {
  '& .MuiToggleButton-root': EM_TOGGLE_BUTTON_SX,
  ...groupEndRadiusSx,
} as const;

/** The chart's group. See `EM_TOGGLE_COMPACT_SX` for why it is not the default. */
export const EM_TOGGLE_GROUP_COMPACT_SX = {
  '& .MuiToggleButton-root': EM_TOGGLE_COMPACT_SX,
  ...groupEndRadiusSx,
} as const;

/**
 * A Select sitting in a toolbar row rather than a form.
 *
 * **The font size is the whole point.** The theme sets inputs to 14px through
 * `MuiTextField`'s `&&& .MuiInputBase-input`, and a bare `Select` is not a `TextField`, so
 * it misses that rule and renders at MUI's default 16px — a control two pixels larger than
 * every other input in the app. `&&&` matches the theme's own specificity rather than
 * hoping to outrank it.
 *
 * **This is the one place the module leaves the app's input metrics.** `GlobalStyles` sets
 * `.MuiInputBase-root` to a 2.5rem floor, which is right for a form field and too tall
 * beside 32px chips — the row reads as two bands of controls sharing a line. The floor is
 * lifted here and nowhere else: `EM_CONTROL_SX`, which dresses the modals and the registry
 * form, keeps the app height.
 *
 * `margin: 0` undoes the `margin: 'dense'` default, meant for stacked fields, which here
 * just offsets the control against the chips beside it.
 */
export const EM_TOOLBAR_SELECT_SX = {
  background: colors.primary[0],
  margin: 0,
  borderRadius: EM_RADIUS,
  // On the root, not the inner input: MUI puts `body1` on `.MuiInputBase-root` and gives
  // the input `font: inherit`, so sizing the input alone leaves the root's 16px in place
  // for anything that measures from it.
  fontFamily: 'Wotfard-Regular',
  fontSize: '13px',
  // `&&` catches a bare Select, where sx lands on the InputBase itself; the descendant
  // catches a TextField, where sx lands on the FormControl and the InputBase is a child.
  // Either way it has to outrank GlobalStyles' 2.5rem floor on `.MuiInputBase-root`.
  '&&, && .MuiInputBase-root': {
    minHeight: '32px',
    borderRadius: EM_RADIUS,
    fontFamily: 'Wotfard-Regular',
    fontSize: '13px',
  },
  '&&& .MuiInputBase-input': { fontFamily: 'Wotfard-Regular', fontSize: '13px' },
  '& .MuiSelect-select': { py: '6px' },
  // MUI floats the outline 5px above the input to clear a floating label, so the painted
  // box is always 5px taller than the control it wraps. These have no label — the row's
  // section labels sit outside — so the offset only made the inputs the tallest thing in it.
  '& .MuiOutlinedInput-notchedOutline': {
    top: 0,
    borderColor: colors.neutral[20],
    borderRadius: EM_RADIUS,
    '& legend': { display: 'none' },
  },
  // The default 24px chevron reads as heavy against 14px text.
  '& .MuiSelect-icon': { fontSize: 18, color: colors.neutral[50] },
} as const;

/**
 * {@link EM_TOOLBAR_SELECT_SX} for an Autocomplete standing in a row of selects.
 *
 * MUI pads a small outlined Autocomplete's root 6px top and bottom on top of the 2.5px on
 * the input itself, which puts the field at 36px beside 32px selects. Trimmed to the
 * select's height; the `&&&` outranks MUI's own size variant, which carries three classes.
 */
export const EM_TOOLBAR_AUTOCOMPLETE_SX = {
  ...EM_TOOLBAR_SELECT_SX,
  '&&& .MuiOutlinedInput-root': { paddingTop: '4px', paddingBottom: '4px' },
} as const;

/** The floor a toolbar picker sizes up from when its value is shorter. */
export const EM_TOOLBAR_PICKER_MIN_WIDTH = 200;

/**
 * Pinned and shared, or entity names of wildly different lengths reflow the toolbar between
 * the two surfaces carrying one. Pass it to {@link emToolbarMenuSx} as well.
 */
export const EM_SUBJECT_SELECT_WIDTH = 320;

/**
 * MUI centres a Select's menu and Popover holds it 16px off the window edge, so it lands
 * beside its field. `marginThreshold: 0`, not `null` — `null` drops the vertical clamp too.
 */
export const EM_TOOLBAR_MENU_ALIGN_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  marginThreshold: 0,
} as const;

/**
 * {@link EM_TOOLBAR_MENU_ALIGN_PROPS} plus a width pin, at the field's own width. Ellipsises
 * an option longer than the field, so it is for row-end selects rather than name lists.
 */
export function emToolbarMenuSx(width: number) {
  return {
    ...EM_TOOLBAR_MENU_ALIGN_PROPS,
    slotProps: {
      paper: {
        sx: {
          width,
          // Roughly ten rows. Past that the list scrolls rather than running off-screen,
          // which is what a register of every portfolio company otherwise does.
          maxHeight: '20rem',
        },
      },
    },
  } as const;
}

/**
 * A group heading inside a toolbar select's menu.
 *
 * `ListSubheader` inherits `subtitle1` — 16px, taller than the options it introduces, so it
 * reads as an entry rather than a label for the entries. Sized to the section labels beside
 * the toolbar's controls instead, which is what it is.
 */
export const EM_TOOLBAR_GROUP_HEADING_SX = {
  lineHeight: '28px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.neutral[50],
  background: colors.primary[0],
} as const;

/**
 * A filter and its menu from one width. Options still need {@link EM_TOOLBAR_MENU_ITEM_SX} —
 * `sx` on a Select dresses the closed field, and the menu rows are portalled children.
 */
export function emToolbarFilterProps(width: number) {
  return {
    sx: { ...EM_TOOLBAR_SELECT_SX, minWidth: width },
    MenuProps: emToolbarMenuSx(width),
  } as const;
}

/** Keeps a name that outgrows {@link emToolbarMenuSx} on one line instead of reflowing the row. */
export const EM_TOOLBAR_MENU_ITEM_SX = {
  fontFamily: 'Wotfard-Regular',
  fontSize: '13px',
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/**
 * The shared `SearchInput` in a toolbar row.
 *
 * `EM_SEARCH_SX` keeps the app's 40px form height, which is right in a form and two bands
 * tall beside 32px chips. This is that field at the row's height.
 *
 * The input padding is restated because the two search boxes in this module are not the
 * same component: the registry's shared `SearchInput` zeroes its own padding, while the
 * slate's plain `TextField` keeps MUI's 8.5px, which alone makes it 4px taller than the
 * control beside it.
 */
export const EM_TOOLBAR_SEARCH_SX = {
  background: colors.primary[0],
  borderRadius: EM_RADIUS,
  // `margin: 'dense'` adds 8px above and 4px below, which is for stacked form fields and
  // here just drops the field 2px below the controls it shares a row with.
  margin: 0,
  '&&, && .MuiInputBase-root': {
    minHeight: '32px',
    borderRadius: EM_RADIUS,
    fontFamily: 'Wotfard-Regular',
    fontSize: '13px',
  },
  '&&& .MuiInputBase-input': { fontFamily: 'Wotfard-Regular', fontSize: '13px', py: '6px' },
  // MUI floats the outline 5px above the input to clear a floating label, so the painted
  // box is always 5px taller than the control it wraps. These have no label — the row's
  // section labels sit outside — so the offset only made the inputs the tallest thing in it.
  '& .MuiOutlinedInput-notchedOutline': {
    top: 0,
    borderColor: colors.neutral[20],
    borderRadius: EM_RADIUS,
    '& legend': { display: 'none' },
  },
} as const;

/**
 * A toolbar action that is not a mode — Auto-arrange runs once and returns.
 *
 * Filled rather than outlined so it does not read as a fourth toggle beside the routing
 * pair, and it takes a hover so it still announces itself as pressable without a border.
 */
export const EM_TOOLBAR_ACTION_SX = {
  height: '32px',
  px: '10px',
  border: 'none',
  borderRadius: EM_RADIUS,
  fontSize: '13px',
  fontWeight: 500,
  color: colors.neutral[70],
  background: colors.neutral[10],
  '&:hover': { background: colors.neutral[20], border: 'none' },
  '&.Mui-disabled': { border: 'none', background: colors.neutral[5] },
  '& .MuiButton-startIcon': { mr: '6px' },
} as const;

/**
 * A filled toolbar action — the one thing on the row a user is expected to press.
 *
 * MUI's small contained button is 31px and 4px-cornered, which is one pixel and one radius
 * away from everything beside it. Close enough to look like a mistake rather than a
 * decision, so both are pinned to the row's.
 */
export const EM_TOOLBAR_PRIMARY_ACTION_SX = {
  height: '32px',
  px: '14px',
  borderRadius: EM_RADIUS,
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;

/**
 * A create action in a toolbar rather than the header. Geometry only: the colour comes from
 * `variant='outlined' color='secondary'`, matching `EntityManagementHeaderActions`.
 */
export const EM_TOOLBAR_CREATE_ACTION_SX = {
  height: '32px',
  px: '12px',
  borderRadius: EM_RADIUS,
  fontSize: '13px',
  fontWeight: 500,
  '& .MuiButton-startIcon': { mr: '6px' },
} as const;

/**
 * A grid row's action button: the toolbar's type and corner, 4px shorter so it leaves air
 * inside the row. MUI's own small outlined button is 31px, one pixel off everything else.
 *
 * Metrics only. A row's actions are ranked by variant and colour, and the neutral border
 * that used to live here flattened the ranks it was applied over — see
 * `EM_ROW_ACTION_OUTLINED_SX`, which is where it went.
 */
export const EM_ROW_ACTION_SX = {
  minWidth: 0,
  height: '28px',
  px: '10px',
  borderRadius: EM_RADIUS,
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
} as const;

/**
 * The row action that is a plain alternative: the metrics above, plus the toolbar's neutral
 * border.
 *
 * Kept off the ranked variants deliberately. `neutral[20]` would pull a `warning` outline
 * back to grey, leaving only the label coloured, and the hover `background` would paint
 * over a `contained` button's fill — each undoing the rank it was meant to carry.
 */
export const EM_ROW_ACTION_OUTLINED_SX = {
  ...EM_ROW_ACTION_SX,
  borderColor: colors.neutral[20],
  '&:hover': { borderColor: colors.neutral[30], background: colors.neutral[5] },
  '&.Mui-disabled': { borderColor: colors.neutral[10] },
} as const;

/**
 * The wrapper a Tooltip needs around a disabled button. Flex, not a bare inline span, which
 * would drop the button onto its own text baseline below the row.
 */
export const emTooltipWrapStyles = css`
  display: flex;
`;

/**
 * The pill geometry every status/role tag in the module shares.
 *
 * Colour is deliberately absent — each surface supplies its own from `roleColors` or its
 * status map. Only the shape is common, and it is the shape that must not drift.
 */
export const emPillBaseStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 10px;
  border-radius: 11px;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  flex: 0 0 auto;
`;

/** Wraps an AgTable so it fills the surface below the toolbar instead of collapsing. */
export const emGridWrapperStyles = css`
  flex: 1;
  min-height: 0;
  width: 100%;
`;

/**
 * A section heading inside an entity record — TAX, ADDRESSES, GOVERNANCE & SERVICE.
 *
 * Shared by the read view and the edit form deliberately. They are the same record under
 * two verbs, and a reader who has just looked at a record grouped one way should not have
 * to re-find a field in a flat list the moment they press Edit.
 */
export const emRecordSectionHeadingStyles = css`
  margin: 0 0 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.neutral[10]};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.neutral[50]};
`;

/**
 * Uppercase label introducing a control group (FUND, FOCUS, RELATIONSHIPS, METRIC).
 *
 * A quiet marker, deliberately below the control text it introduces. The theme's
 * `tpgrOverline` is not the reference: it is 12px but has no consumer anywhere in
 * `src/pages`, so matching it would align this row with nothing.
 *
 * Line height is pinned because an unpinned one is set for stacked headings and would
 * drive the toolbar row's height from the label rather than the controls beside it.
 */
export const emSectionLabelStyles = css`
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.neutral[50]};
  white-space: nowrap;
`;
